/**
 * Gate pass — a physical TICKET: fixed brand-green header band, faux-QR +
 * big mono PIN, a perforated edge (two canvas-colored notch circles + a
 * dashed line) between the QR block and the guest/visiting/valid/pass-id
 * facts. Port of screens-services.jsx GatePass. Receives id/name/code/valid
 * params from /preapprove; falls back to demo values when opened directly.
 */
import { router, useLocalSearchParams } from 'expo-router';
import React from 'react';
import { Platform, Pressable, Share, Text as RNText, View } from 'react-native';
import * as Sharing from 'expo-sharing';
import { captureRef } from 'react-native-view-shot';

import { glow, palette, radius, space } from '@bero/tokens';
import { InfoNote, QRBox } from '@/components/gate/bits';
import { useT, translate, monthShort } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';
import { useSession } from '@/lib/session';
import { useTheme } from '@/theme';
import { AppText, Button, EmptyState, Pill, Screen, Sheet, Skeleton, TopBar } from '@/ui';

type NavigatorShare = {
  share?: (data: { title?: string; text?: string; files?: File[] }) => Promise<void>;
  canShare?: (data: { files?: File[] }) => boolean;
};
type NavigatorClipboard = { clipboard?: { writeText?: (text: string) => Promise<void> } };

/** Fixed brand-green chrome for the ticket's header band — see
 * bero-tokens.css README note: solid green panels use fixed dark-brand hex
 * in both app themes, like a printed pass. */
const BRAND = palette.light;
const BRAND_FIGURE = palette.dark.accent500;

function TicketMark({ height = 20 }: { height?: number }) {
  const u = height / 58;
  return (
    <View style={{ width: 50 * u, height }}>
      <View style={{
        position: 'absolute', left: 2 * u, top: 1 * u, width: 46 * u, height: 55 * u,
        borderWidth: 9 * u, borderBottomWidth: 0, borderColor: BRAND.canvas0,
        borderTopLeftRadius: 23 * u, borderTopRightRadius: 23 * u,
      }} />
      <View style={{ position: 'absolute', left: 19 * u, top: 24 * u, width: 12 * u, height: 12 * u, borderRadius: 6 * u, backgroundColor: BRAND_FIGURE }} />
      <View style={{ position: 'absolute', left: 20 * u, top: 40 * u, width: 10 * u, height: 16 * u, borderRadius: 5 * u, backgroundColor: BRAND_FIGURE }} />
    </View>
  );
}

/** The ticket is a fixed dark lacquer object in BOTH themes — deep forest
 * paper, cream ink, one amber accent (the PIN). Shares capture exactly this. */
const TICKET = {
  paper: palette.light.primary900,   // #15281A deep forest
  rule: palette.light.primary700,    // tonal hairline on the paper
  ink: palette.light.canvas0,        // cream
  inkMuted: palette.light.primary100,
  accent: palette.dark.accent500,    // amber tuned for dark surfaces
};

/** Ticket fact row — label anchored, value fills right-aligned, never collides. */
function FactRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: space.s4 }}>
      <AppText variant="bodySm" style={{ flexShrink: 0, paddingTop: 1, color: TICKET.inkMuted, opacity: 0.75 }}>{label}</AppText>
      <AppText variant="bodySm" weight="600" mono={mono} style={{ flex: 1, textAlign: 'right', color: TICKET.ink }}>
        {value}
      </AppText>
    </View>
  );
}

/** The signature: editorial mega-type reading up the left rail, like a
 * lanyard badge. Rotated Sora — measured at runtime so it always fits. */
function GuestRail({ word = 'GUEST' }: { word?: string }) {
  const [railH, setRailH] = React.useState(0);
  return (
    <View
      onLayout={(e) => setRailH(e.nativeEvent.layout.height)}
      style={{ width: 56, alignSelf: 'stretch', alignItems: 'center', justifyContent: 'center' }}
    >
      {railH > 0 ? (
        <RNText
          numberOfLines={1}
          style={{
            position: 'absolute', width: railH, minWidth: railH, maxWidth: railH, height: 48, textAlign: 'center',
            left: (56 - railH) / 2, top: railH / 2 - 24,
            transform: [{ rotate: '-90deg' }],
            fontFamily: 'Sora_800ExtraBold', fontSize: 34, lineHeight: 48,
            letterSpacing: 10, color: TICKET.ink, opacity: 0.95,
          }}
        >
          {word}
        </RNText>
      ) : null}
    </View>
  );
}

type ActivePass = { id: number; pin: string; validFrom?: string; validUntil?: string; visitorName?: string; multiUse?: boolean };
type PassStatus = 'active' | 'upcoming' | 'expired' | 'cancelled';
type ListPass = ActivePass & { status: PassStatus; shareCode?: string };

/** "7 Jul · 6:00–10:00 PM" for a pass's window. */
function windowLabel(p: ListPass): string {
  const from = p.validFrom ? new Date(p.validFrom) : null;
  const until = p.validUntil ? new Date(p.validUntil) : null;
  const t = (d: Date) => {
    let h = d.getHours(); const am = h < 12 ? 'AM' : 'PM'; h = h % 12 || 12;
    return `${h}:${String(d.getMinutes()).padStart(2, '0')} ${am}`;
  };
  if (from && until && !Number.isNaN(from.getTime())) {
    return `${from.getDate()} ${monthShort(from.getMonth())} · ${t(from)} – ${t(until)}`;
  }
  return p.multiUse ? translate('gp_multi_entry') : translate('gp_single_entry');
}

const STATUS_PILL: Record<PassStatus, { pill: 'approved' | 'pending' | 'rejected' | 'open'; label: DictKey }> = {
  active: { pill: 'approved', label: 'pill_active_now' },
  upcoming: { pill: 'pending', label: 'pill_upcoming' },
  expired: { pill: 'open', label: 'pill_expired' },
  cancelled: { pill: 'rejected', label: 'pill_cancelled' },
};

/** List of all passes the flat has issued — the Gate-tab entry point. */
function PassList() {
  const { colors } = useTheme();
  const { t } = useT();
  const [items, setItems] = React.useState<ListPass[] | null>(null);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const body = await api.get<{ items: ListPass[] }>('/api/visitors/gatepass/mine');
        if (!cancelled) setItems(Array.isArray(body.items) ? body.items : []);
      } catch (e) {
        // No flat on this account (admin/guard) → calm empty, not an error.
        const noFlat = (e as { status?: number; error?: string })?.status === 400
          && /no flat/i.test((e as { error?: string })?.error ?? '');
        if (!cancelled) { setItems([]); setError(!noFlat); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const open = (p: ListPass) => {
    const params = new URLSearchParams({
      id: String(p.id), code: p.pin, name: p.visitorName ?? 'Guest',
      valid: validLabel(p), sc: p.shareCode ?? '',
    }).toString();
    router.push(`/gatepass?${params}`);
  };

  return (
    <View style={{ flex: 1, backgroundColor: colors.canvas0 }}>
      <TopBar
        title={t('gp_list_title')}
        onBack={() => router.back()}
        right={
          <Pressable accessibilityRole="button" accessibilityLabel={t('gp_new')} onPress={() => router.push('/preapprove')} hitSlop={8} style={{ paddingHorizontal: space.s2 }}>
            <AppText variant="bodySm" weight="600" tone="brand">{t('gp_new')}</AppText>
          </Pressable>
        }
      />
      <Screen padded style={{ paddingTop: space.s4 }}>
        {items === null ? (
          <View style={{ gap: space.s3 }}>{[0, 1, 2].map((i) => <Skeleton key={i} h={72} />)}</View>
        ) : items.length === 0 ? (
          <EmptyState
            title={error ? t('gp_empty_err_title') : t('gp_empty_title')}
            body={error ? t('gp_empty_err_body') : t('gp_empty_body')}
            cta={t('gp_preapprove_cta')}
            onCta={() => router.push('/preapprove')}
          />
        ) : (
          <View style={{ gap: space.s3 }}>
            {items.map((p) => {
              const s = STATUS_PILL[p.status];
              return (
                <Pressable
                  key={p.id}
                  accessibilityRole="button"
                  onPress={() => open(p)}
                  style={({ pressed }) => ({
                    borderRadius: radius.lg, borderWidth: 1, borderColor: colors.borderSoft,
                    backgroundColor: pressed ? colors.canvas1 : colors.canvas0, padding: space.s4,
                    opacity: p.status === 'expired' || p.status === 'cancelled' ? 0.6 : 1,
                  })}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <AppText variant="bodyMd" weight="600" numberOfLines={1} style={{ flex: 1 }}>
                      {p.visitorName ?? 'Guest'}
                    </AppText>
                    <Pill status={s.pill} label={t(s.label)} />
                  </View>
                  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                    <AppText variant="bodySm" tone="tertiary">{windowLabel(p)}</AppText>
                    <AppText variant="bodySm" mono weight="600" style={{ letterSpacing: 2, color: colors.textSecondary }}>{p.pin}</AppText>
                  </View>
                </Pressable>
              );
            })}
          </View>
        )}
      </Screen>
    </View>
  );
}

function validLabel(p: ActivePass): string {
  const until = p.validUntil ? new Date(p.validUntil) : null;
  const use = p.multiUse ? 'multi entry' : 'single entry';
  if (until && !Number.isNaN(until.getTime())) {
    let h = until.getHours();
    const am = h < 12 ? 'AM' : 'PM';
    h = h % 12 || 12;
    const mm = String(until.getMinutes()).padStart(2, '0');
    // No leading "Valid" — the ticket's fact-row label is already "Valid".
    return `Today · until ${h}:${mm} ${am} · ${use}`;
  }
  return `Today · ${use}`;
}

function formatFlat(code?: string): string {
  if (!code) return '—';
  return code.replace(/^([A-Za-z]+)(\d+)$/, '$1-$2');
}

export default function GatePassScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { user } = useSession();
  const params = useLocalSearchParams<{ id?: string; name?: string; code?: string; valid?: string; sc?: string }>();

  // No params (opened from the Gate tab) → show the LIST of issued passes.
  // A specific pass is opened WITH params (from the list, or from preapprove).
  const [copied, setCopied] = React.useState(false);
  const [shareNotice, setShareNotice] = React.useState<string | null>(null);
  const [revokeSheetOpen, setRevokeSheetOpen] = React.useState(false);
  const [revoking, setRevoking] = React.useState(false);
  const [revoked, setRevoked] = React.useState(false);
  const [revokeError, setRevokeError] = React.useState<string | null>(null);
  // While the web capture runs, the controls below the ticket are faded out —
  // html2canvas mis-positions translucent siblings into the capture otherwise.
  const [capturing, setCapturing] = React.useState(false);
  const ticketRef = React.useRef<View>(null);

  // No specific pass requested → list all issued passes.
  if (!params.code) {
    return <PassList />;
  }

  const name = params.name || 'Guest';
  const code = (params.code || '').slice(0, 6);
  const codeSpaced = code.split('').join(' ');
  const valid = params.valid || 'Valid today · single entry';
  const rawId = params.id ?? '';
  const passId = rawId ? `GP-${rawId}` : 'GP';
  const seed = `BERO-${rawId || code}`;
  const shareLink = params.sc ? `https://usebero.com/p/${params.sc}` : null;
  // Prefer the live link (opens the full visual pass in any browser, revocable,
  // no app needed); fall back to the code text if no share code is present.
  const shareText = shareLink
    ? t('gp_share_text_link', { name, link: shareLink })
    : t('gp_share_text_code', { name, code: codeSpaced, valid });

  const canShareWeb = Platform.OS === 'web' && typeof navigator !== 'undefined' && !!(navigator as NavigatorShare).share;
  const canClipboardWeb = Platform.OS === 'web' && typeof navigator !== 'undefined' && !!(navigator as NavigatorClipboard).clipboard?.writeText;

  const doCopy = async () => {
    if (!canClipboardWeb) return;
    try {
      await (navigator as NavigatorClipboard).clipboard!.writeText!(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      setShareNotice(t('gp_copy_error'));
    }
  };

  const doShare = async () => {
    // Native: capture the ticket as a PNG and share image + live link together
    // (the "both link and image" decision). Falls back to text if capture fails.
    if (Platform.OS !== 'web') {
      try {
        const uri = await captureRef(ticketRef, { format: 'png', quality: 1, result: 'tmpfile' });
        if (Platform.OS === 'ios') {
          // iOS share sheet carries an attachment AND the message/link.
          await Share.share({ message: shareText, url: uri });
        } else {
          // Android attachments go through expo-sharing (core Share is text-only).
          await Sharing.shareAsync(uri, { mimeType: 'image/png', dialogTitle: t('gp_share_dialog', { name }) });
        }
      } catch {
        try {
          await Share.share({ message: shareText });
        } catch {
          // user dismissed the share sheet — nothing to surface
        }
      }
      return;
    }
    // Web: capture the designed ticket as a PNG (view-shot 5 renders via
    // html2canvas here) and share the IMAGE when the browser supports file
    // sharing (Safari/mobile). Text-only sharing sent "just links" — user
    // report 2026-07-19. Browsers without file share get a PNG download +
    // the text/link on the clipboard.
    let ticketFile: File | null = null;
    // html2canvas (view-shot's web renderer) draws content inside scrolled
    // ancestors SHIFTED — the share button was bleeding into the capture.
    // Zero every ancestor's scroll for the capture, restore right after.
    const scrolled: [HTMLElement, number][] = [];
    try {
      const el = ticketRef.current as unknown as HTMLElement | null;
      for (let a = el?.parentElement; a; a = a.parentElement) {
        if (a.scrollTop) { scrolled.push([a, a.scrollTop]); a.scrollTop = 0; }
      }
    } catch { /* non-DOM ref — capture may still work */ }
    try {
      setCapturing(true);
      await new Promise((r) => setTimeout(r, 80));
      const dataUri = await captureRef(ticketRef, { format: 'png', quality: 1, result: 'data-uri' });
      const blob = await (await fetch(dataUri)).blob();
      ticketFile = new File([blob], `bero-gate-pass-${(name || 'guest').replace(/\s+/g, '-').toLowerCase()}.png`, { type: 'image/png' });
    } catch {
      // capture failed — fall through to text-only paths below.
    } finally {
      setCapturing(false);
      scrolled.forEach(([el, top]) => { el.scrollTop = top; });
    }
    const nav = navigator as NavigatorShare;
    if (ticketFile && canShareWeb && nav.canShare?.({ files: [ticketFile] })) {
      try {
        await nav.share!({ files: [ticketFile], title: t('gp_share_title'), text: shareText });
      } catch {
        // user cancelled the native share sheet — not an error worth surfacing
      }
      return;
    }
    if (ticketFile && typeof document !== 'undefined') {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(ticketFile);
      a.download = ticketFile.name;
      a.click();
      URL.revokeObjectURL(a.href);
      if (canClipboardWeb) await doCopy();
      return;
    }
    if (canShareWeb) {
      try {
        await nav.share!({ title: t('gp_share_title'), text: shareText });
      } catch {
        // user cancelled the native share sheet — not an error worth surfacing
      }
      return;
    }
    if (canClipboardWeb) {
      await doCopy();
    }
  };

  const doRevoke = async () => {
    if (!rawId || revoking) return;
    setRevoking(true);
    setRevokeError(null);
    try {
      await api.patch<unknown>(`/api/visitors/gatepass/${rawId}`, { isActive: false });
      setRevoked(true);
      setRevokeSheetOpen(false);
    } catch {
      setRevokeError(t('gp_revoke_error'));
    } finally {
      setRevoking(false);
    }
  };

  if (revoked) {
    return (
      <Screen scroll={false} style={{ backgroundColor: colors.canvas1 }}>
        <TopBar title={t('gp_title')} onBack={() => router.back()} />
        <EmptyState
          title={t('gp_cancelled_title')}
          body={t('gp_cancelled_body', { name })}
          cta={t('gp_preapprove_another')}
          onCta={() => router.push('/preapprove')}
        />
      </Screen>
    );
  }

  return (
    <Screen scroll={false} style={{ backgroundColor: colors.canvas1 }}>
      <TopBar
        title={t('gp_title')}
        onBack={() => router.back()}
        right={
          <Pressable accessibilityRole="button" accessibilityLabel={t('gp_share')} onPress={() => void doShare()} hitSlop={8} style={{ paddingHorizontal: space.s2 }}>
            <AppText variant="bodySm" weight="600" tone="brand">{t('gp_share')}</AppText>
          </Pressable>
        }
      />
      <Screen padded style={{ paddingTop: space.s5, alignItems: 'center', backgroundColor: colors.canvas1 }}>
        {/* the ticket — ref'd so Share captures it as a PNG. A fixed dark
            lacquer badge in both themes: deep forest paper, cream ink,
            rotated GUEST rail, amber PIN. */}
        <View ref={ticketRef} collapsable={false} style={{
          width: '100%', maxWidth: 340, alignSelf: 'center',
          backgroundColor: TICKET.paper, borderRadius: radius.xxl,
          overflow: 'hidden',
          // Hero artifact: soft primary halo, not a drop shadow (no-shadows rule).
          // Dropped while capturing — html2canvas paints the box-shadow as a
          // stray band INSIDE the ticket, and a shared PNG needs no halo.
          ...(capturing ? null : glow(colors.primary500, 0.35, 24)),
        }}>
          {/* brand row on the paper itself — one surface, no band */}
          <View style={{ paddingTop: space.s5, paddingHorizontal: space.s5, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <TicketMark height={20} />
            <RNText style={{ fontFamily: 'Sora_800ExtraBold', fontSize: 16, letterSpacing: -0.64, color: TICKET.ink }}>bero</RNText>
            <AppText variant="label" style={{ marginLeft: 'auto', color: TICKET.inkMuted, opacity: 0.8 }}>One Meraki</AppText>
          </View>

          {/* rail | rule | content */}
          <View style={{ flexDirection: 'row', paddingVertical: space.s5, paddingRight: space.s5 }}>
            <GuestRail />
            <View style={{ width: 1, backgroundColor: TICKET.rule, marginRight: space.s5 }} />
            <View style={{ flex: 1, alignItems: 'center' }}>
              <RNText
                numberOfLines={2}
                style={{
                  fontFamily: 'Sora_800ExtraBold', fontSize: 22, lineHeight: 28,
                  letterSpacing: -0.4, color: TICKET.ink, textAlign: 'center', alignSelf: 'stretch',
                }}
              >
                {name}
              </RNText>
              <View style={{ marginTop: space.s4, borderRadius: radius.lg }}>
                <QRBox size={150} seed={seed} />
              </View>
              <AppText variant="label" style={{ marginTop: space.s4, color: TICKET.inkMuted, opacity: 0.75 }}>Or tell the guard</AppText>
              <AppText
                mono weight="600" numberOfLines={1}
                style={{ fontSize: 30, lineHeight: 38, letterSpacing: 8, marginTop: 4, textAlign: 'center', color: TICKET.accent }}
              >
                {code}
              </AppText>
            </View>
          </View>

          <View style={{ height: 1, backgroundColor: TICKET.rule, marginHorizontal: space.s5 }} />

          <View style={{ padding: space.s5, gap: space.s2 }}>
            <FactRow label="Visiting" value={`${user?.fullName ?? 'Resident'} · ${formatFlat(user?.flat?.code)}`} />
            <FactRow label={t('pp_valid_label')} value={valid} />
            <FactRow label={t('gp_pass_id')} value={passId} mono />
          </View>
        </View>

        <View style={{ width: '100%', opacity: capturing ? 0 : 1 }}>
        <View style={{ width: '100%', maxWidth: 340, alignSelf: 'center', marginTop: space.s5, gap: space.s2 }}>
          <Button
            title={t('gp_share_to_guest')}
            onPress={() => void doShare()}
          />
          {/* Copy is web-only: native RN core has no clipboard (expo-clipboard is
              quarantined until build #2). The share sheet covers copy on native. */}
          {Platform.OS === 'web' && canClipboardWeb ? (
            <Button
              title={copied ? t('gp_copied') : t('gp_copy_code')}
              kind="tertiary"
              onPress={() => void doCopy()}
            />
          ) : null}
        </View>

        {shareNotice ? (
          <AppText variant="bodySm" tone="danger" style={{ marginTop: space.s3, width: '100%', maxWidth: 340, alignSelf: 'center' }}>{shareNotice}</AppText>
        ) : null}

        <InfoNote icon="shield" tint={colors.primary500} style={{ marginTop: space.s4, width: '100%', maxWidth: 340, alignSelf: 'center' }}>
          {t('gp_info_note')}
        </InfoNote>

        {rawId ? (
          <Button
            title={t('gp_cancel_pass')}
            kind="destructive"
            style={{ marginTop: space.s4, width: '100%', maxWidth: 340, alignSelf: 'center' }}
            onPress={() => { setRevokeError(null); setRevokeSheetOpen(true); }}
          />
        ) : null}
        </View>
      </Screen>

      <Sheet visible={revokeSheetOpen} onClose={() => { if (!revoking) setRevokeSheetOpen(false); }}>
        <AppText variant="headingMd" weight="500" style={{ marginBottom: space.s2 }}>{t('gp_revoke_hdr')}</AppText>
        <AppText variant="bodyMd" tone="secondary" style={{ marginBottom: space.s5 }}>
          {t('gp_revoke_body', { name })}
        </AppText>
        {revokeError ? (
          <AppText variant="bodySm" tone="danger" style={{ marginBottom: space.s3 }}>{revokeError}</AppText>
        ) : null}
        <Button title={t('gp_keep_pass')} kind="secondary" disabled={revoking} onPress={() => setRevokeSheetOpen(false)} />
        <Button
          title={revoking ? t('gp_cancelling') : t('gp_cancel_pass')}
          kind="destructive"
          loading={revoking}
          style={{ marginTop: space.s2 }}
          onPress={() => void doRevoke()}
        />
      </Sheet>
    </Screen>
  );
}
